/**
 * Series labelling and colour — contract series → chart props.
 *
 * Colour rules are the prototype's (`data/aggregate.ts`): sentiment is pinned
 * (positive/neutral/negative = chart-4/5/6); any other dimension value takes
 * the palette slot of its position in the server's option order, wrapping at
 * the palette size; a single metric series is chart-1.
 */
import {
  chartPaletteSize,
  chartSeriesTokens,
  type ChartRow,
  type ChartSeries,
  type ChartTooltipComparison,
  type ChartSeriesToken,
} from '@deepdive-ui';

import type { DimensionKey, FilterOptions } from '@/data-sources/contract/filters';
import type { SeriesKey, TimeSeries } from '@/data-sources/contract/primitives';
import { readableLabel } from '@/lib/labels';

import { bucketLabel } from './buckets';
import { isoToMs } from './dates';
import { comparisonReading } from './kpi';
import { metricMeta, type MetricId } from './metrics';

/** The x key every chart row uses. Asserted collision-free by the mock engine. */
export const X_KEY = '__x';

/**
 * The key suffix a series' comparison-period value takes on a chart row. Not a
 * plotted series — no `ChartSeries` names it — so it rides along on the row for
 * the tooltip and is never drawn.
 */
export const PREV_SUFFIX = '__prev';
export const prevKey = (seriesKey: string) => `${seriesKey}${PREV_SUFFIX}`;

export const SENTIMENT_TOKENS: Readonly<Record<string, ChartSeriesToken>> = {
  positive: 'chart-4',
  neutral: 'chart-5',
  negative: 'chart-6',
};

const SENTIMENT_ORDER = ['positive', 'neutral', 'negative'] as const;
export type Sentiment = (typeof SENTIMENT_ORDER)[number];

export function optionIndex(
  options: FilterOptions,
  dimension: DimensionKey,
  value: string,
): number {
  const index = options[dimension].findIndex((option) => option.value === value);
  return index < 0 ? 0 : index;
}

/** An option's label, readable (`digital_banking` → `Digital Banking`); an unknown key reads as itself. */
export function labelOf(options: FilterOptions, dimension: DimensionKey, value: string): string {
  return readableLabel(options[dimension].find((option) => option.value === value)?.label ?? value);
}

/**
 * Brands drawn in their own colours, by name (GP's ask, 2026-11-01), so one
 * entry holds on staging, production and the sample data. Colours are from each
 * brand's site where it has a usable one (docs/design/rules.md); any other
 * brand takes the ramp by its place in the roster.
 */
const BRAND_TOKENS: Readonly<Record<string, ChartSeriesToken>> = {
  grameenphone: 'chart-brand-grameenphone',
  robi: 'chart-brand-robi',
  airtel: 'chart-brand-airtel',
  banglalink: 'chart-brand-banglalink',
  teletalk: 'chart-brand-teletalk',
  skitto: 'chart-brand-skitto',
  ryze: 'chart-brand-ryze',
  cirkle: 'chart-brand-cirkle',
};

export function seriesToken(
  options: FilterOptions,
  dimension: DimensionKey,
  value: string,
): ChartSeriesToken {
  if (dimension === 'brand') {
    const name = options.brand.find((option) => option.value === value)?.label ?? value;
    const own = BRAND_TOKENS[name.trim().toLowerCase()];
    if (own) return own;
  }
  if (dimension === 'sentiment') {
    const byMeaning = SENTIMENT_TOKENS[value];
    if (byMeaning) return byMeaning;
  }
  return chartSeriesTokens[optionIndex(options, dimension, value) % chartPaletteSize]!;
}

/** Contract series → chart series (label + colour). */
export function chartSeriesFor(options: FilterOptions, keys: readonly SeriesKey[]): ChartSeries[] {
  return keys.map((s) =>
    s.dimension
      ? {
          key: s.key,
          label: labelOf(options, s.dimension, s.key),
          color: seriesToken(options, s.dimension, s.key),
        }
      : {
          key: s.key,
          label: metricMeta[s.key as MetricId]?.label ?? s.key,
          color: chartSeriesTokens[0]!,
        },
  );
}

export type TimeChart = { rows: ChartRow[]; xKey: typeof X_KEY; series: ChartSeries[] };

/** A contract time series as Recharts rows: one row per bucket, one key per series. */
export function timeChart(options: FilterOptions, ts: TimeSeries): TimeChart {
  const series = chartSeriesFor(options, ts.series);
  const rows: ChartRow[] = ts.x.map((iso, i) => {
    const row: ChartRow = { [X_KEY]: bucketLabel(isoToMs(iso), ts.bucket) };
    for (const s of ts.series) row[s.key] = s.values[i] ?? 0;
    if (ts.previous) {
      for (const s of ts.previous) row[prevKey(s.key)] = s.values[i] ?? null;
    }
    return row;
  });
  return { rows, xKey: X_KEY, series };
}

/**
 * The tooltip's comparison reader for rows carrying `prevKey` values: the
 * previous figure in the metric's format, and the change in its delta mode.
 * Nothing for a row without one — comparison off, or no matching bucket.
 *
 * `currentKey` is where the row keeps the figure compared, when the plotted
 * value is not it — an indexed line keeps its count under `countKey`.
 */
export function compareFormatter(
  metricId: MetricId,
  currentKey: (seriesKey: string) => string = (seriesKey) => seriesKey,
) {
  return (row: ChartRow, seriesKey: string): ChartTooltipComparison | undefined => {
    const previous = row[prevKey(seriesKey)];
    if (previous === null || previous === undefined || previous === '') return undefined;
    return comparisonReading(metricId, Number(row[currentKey(seriesKey)] ?? 0), Number(previous));
  };
}

export function dominantSentiment(
  counts: Readonly<Record<string, number>>,
): { sentiment: Sentiment; token: ChartSeriesToken; count: number; share: number } | null {
  let best: Sentiment | null = null;
  let bestCount = 0;
  let total = 0;
  for (const sentiment of SENTIMENT_ORDER) {
    const count = counts[sentiment] ?? 0;
    total += count;
    if (count > bestCount) {
      best = sentiment;
      bestCount = count;
    }
  }
  if (best === null || total === 0) return null;
  return {
    sentiment: best,
    token: SENTIMENT_TOKENS[best]!,
    count: bestCount,
    share: (bestCount / total) * 100,
  };
}

/** A categorical chart's row and the key of the category it draws — what `withPrevious` matches on. */
export type KeyedRow = readonly [key: string, row: ChartRow];

/**
 * A categorical chart's rows carrying the comparison period's figures, under
 * the same `prevKey` a time series' bucket carries them, so `compareFormatter`
 * reads both.
 *
 * Each category is matched to itself by **key** — never by its label, which
 * can be cut short or shared (two long community names, two voices of one
 * name), nor by position, because a ranked axis reorders between periods.
 * A category the comparison period lacks reads 0: it was not mentioned then.
 * `previous` is every category of the comparison period, not only the ones
 * drawn, so one ranked lower then still has its figure. Without it (comparison
 * off, or a source that sends none) the rows come back as they are.
 */
export function withPrevious(
  current: readonly KeyedRow[],
  previous: readonly KeyedRow[] | null | undefined,
  seriesKeys: readonly string[],
): ChartRow[] {
  if (!previous) return current.map(([, row]) => row);
  const before = new Map(previous);
  return current.map(([key, row]) => {
    const prior = before.get(key);
    const next: ChartRow = { ...row };
    for (const seriesKey of seriesKeys) next[prevKey(seriesKey)] = Number(prior?.[seriesKey] ?? 0);
    return next;
  });
}
